import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Calendar, ChevronDown, X, Check } from 'lucide-react';
import { format, parse, isValid } from 'date-fns';
import { DatePopover } from '../ui/date-popover';
import { cn } from '../../lib/utils';

/**
 * Reports header controls — the date-range chip + filters chip used at the
 * top of the Admin Reports page.
 *
 * Date range supports quick presets (MTD / QTD / YTD / Last Month / This Week
 * / 7 days / 30 days / 90 days) plus a custom start/end picker. Pure
 * controlled component — the parent owns the range and re-derives the
 * windowed lead set whenever it changes.
 *
 * Filters supports multi-select on LOs, Loan Purpose, and Channel — each
 * group "off" = no filter for that dimension. The chip displays a count
 * badge whenever any filter is active so the broker knows the headline
 * numbers are scoped.
 */
export const PRESETS = [
  { key: 'mtd',    label: 'Month to Date' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'this_week', label: 'This Week' },
  { key: 'last_7',  label: 'Last 7 days' },
  { key: 'last_30', label: 'Last 30 days' },
  { key: 'last_90', label: 'Last 90 days' },
  { key: 'qtd',    label: 'Quarter to Date' },
  { key: 'ytd',    label: 'Year to Date' },
];

const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const fmtFull = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
export const rangeLabel = ({ start, end, preset }) => {
  if (preset && preset !== 'custom') {
    const p = PRESETS.find((x) => x.key === preset);
    if (p) return p.label;
  }
  return `${fmt(start)} – ${fmtFull(end)}`;
};

export function presetRange(key, ref = new Date()) {
  const today = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
  const out = { preset: key };
  switch (key) {
    case 'mtd':
      out.start = new Date(today.getFullYear(), today.getMonth(), 1);
      out.end   = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      break;
    case 'last_month':
      out.start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      out.end   = new Date(today.getFullYear(), today.getMonth(), 0);
      break;
    case 'this_week': {
      const dow = today.getDay();
      const monday = new Date(today);
      monday.setDate(today.getDate() - ((dow + 6) % 7));
      out.start = monday;
      out.end   = new Date(monday);
      out.end.setDate(monday.getDate() + 6);
      break;
    }
    case 'last_7':
      out.end = today;
      out.start = new Date(today);
      out.start.setDate(today.getDate() - 6);
      break;
    case 'last_30':
      out.end = today;
      out.start = new Date(today);
      out.start.setDate(today.getDate() - 29);
      break;
    case 'last_90':
      out.end = today;
      out.start = new Date(today);
      out.start.setDate(today.getDate() - 89);
      break;
    case 'qtd': {
      const q = Math.floor(today.getMonth() / 3);
      out.start = new Date(today.getFullYear(), q * 3, 1);
      out.end   = new Date(today.getFullYear(), q * 3 + 3, 0);
      break;
    }
    case 'ytd':
      out.start = new Date(today.getFullYear(), 0, 1);
      out.end   = new Date(today.getFullYear(), 11, 31);
      break;
    default:
      out.start = new Date(today.getFullYear(), today.getMonth(), 1);
      out.end   = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  }
  return out;
}

const isoOf = (d) => format(d, 'yyyy-MM-dd');
const fromIso = (s) => {
  if (!s) return null;
  const d = parse(s, 'yyyy-MM-dd', new Date());
  return isValid(d) ? d : null;
};

/* ------------------------------------------------------------------ */
/* Date-range chip + popover                                          */
/* ------------------------------------------------------------------ */
export function DateRangeChip({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!ref.current) return;
      const inWrap = ref.current.contains(e.target);
      const inCal = e.target.closest('[data-radix-popper-content-wrapper]');
      if (!inWrap && !inCal) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Local draft for the custom start/end — only commits on Apply.
  const [draftStart, setDraftStart] = useState(isoOf(value.start));
  const [draftEnd, setDraftEnd] = useState(isoOf(value.end));
  useEffect(() => {
    setDraftStart(isoOf(value.start));
    setDraftEnd(isoOf(value.end));
  }, [value]);

  const pickPreset = (key) => {
    const r = presetRange(key);
    onChange(r);
    setOpen(false);
  };
  const applyCustom = () => {
    const s = fromIso(draftStart);
    const e = fromIso(draftEnd);
    if (!s || !e || s > e) return;
    onChange({ start: s, end: e, preset: 'custom' });
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="reports-range"
        onClick={() => setOpen((v) => !v)}
        className="h-10 px-4 rounded-xl border hairline bg-white text-[12.5px] font-semibold text-neutral-900 hover:border-neutral-400 transition inline-flex items-center gap-2"
      >
        <Calendar className="h-3.5 w-3.5 text-neutral-500" />
        {rangeLabel(value)}
        <ChevronDown className="h-3.5 w-3.5 text-neutral-400" />
      </button>
      {open && (
        <div
          data-testid="reports-range-popover"
          className="absolute right-0 top-full mt-2 z-30 w-[420px] rounded-2xl border hairline bg-white shadow-[0_24px_60px_-24px_rgba(0,0,0,0.45)] overflow-hidden"
        >
          <div className="grid grid-cols-[160px_1fr] divide-x hairline-soft">
            {/* Preset rail */}
            <div className="py-2">
              {PRESETS.map((p) => {
                const active = value.preset === p.key;
                return (
                  <button
                    key={p.key}
                    data-testid={`reports-range-preset-${p.key}`}
                    onClick={() => pickPreset(p.key)}
                    className={cn(
                      'w-full text-left px-3 py-2 text-[12.5px] font-semibold transition',
                      active
                        ? 'bg-neutral-900 text-white'
                        : 'text-neutral-700 hover:bg-neutral-50',
                    )}
                  >
                    {p.label}
                  </button>
                );
              })}
              <button
                data-testid="reports-range-preset-custom"
                onClick={() => onChange({ ...value, preset: 'custom' })}
                className={cn(
                  'w-full text-left px-3 py-2 text-[12.5px] font-semibold transition',
                  value.preset === 'custom'
                    ? 'bg-neutral-900 text-white'
                    : 'text-neutral-700 hover:bg-neutral-50',
                )}
              >
                Custom range
              </button>
            </div>
            {/* Custom range body */}
            <div className="p-4">
              <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-1.5 block">Start</span>
              <DatePopover testId="reports-range-start" value={draftStart} onChange={setDraftStart} />
              <div className="h-3" />
              <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-1.5 block">End</span>
              <DatePopover testId="reports-range-end" value={draftEnd} onChange={setDraftEnd} min={draftStart} />
              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={() => setOpen(false)}
                  className="h-9 px-3.5 rounded-lg border hairline bg-white text-[12px] font-bold text-neutral-700 hover:border-neutral-400 transition"
                >
                  Cancel
                </button>
                <button
                  data-testid="reports-range-apply"
                  onClick={applyCustom}
                  className="h-9 px-3.5 rounded-lg bg-neutral-900 text-white text-[12px] font-bold hover:bg-neutral-800 transition"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Filters chip + popover                                             */
/* ------------------------------------------------------------------ */
export const PURPOSE_OPTIONS = [
  { key: 'purchase',   label: 'Purchase' },
  { key: 'refinance',  label: 'Refinance' },
];
export const CHANNEL_OPTIONS = [
  { key: 'broker',        label: 'Broker' },
  { key: 'correspondent', label: 'Correspondent' },
];
export const STATUS_OPTIONS = [
  { key: 'ready_to_submit',          label: 'Ready to Submit' },
  { key: 'submitted_to_uw',          label: 'Submitted' },
  { key: 'approved_with_conditions', label: 'Approved With Conditions' },
  { key: 'clear_to_close',           label: 'Cleared to Closed' },
  { key: 'funded',                   label: 'Funded' },
];

export function activeFilterCount(filters) {
  return (filters.loIds?.size || 0)
    + (filters.purposes?.size || 0)
    + (filters.channels?.size || 0)
    + (filters.sources?.size || 0)
    + (filters.lenders?.size || 0)
    + (filters.states?.size || 0)
    + (filters.statuses?.size || 0);
}

export function FiltersChip({ value, onChange, team, sourceOptions = [], lenderOptions = [], stateOptions = [], hideChannel = false, hideLender = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = (group, key) => {
    const next = new Set(value[group] || []);
    if (next.has(key)) next.delete(key); else next.add(key);
    onChange({ ...value, [group]: next });
  };
  const clear = () => onChange({
    loIds: new Set(), purposes: new Set(), channels: new Set(),
    sources: new Set(), lenders: new Set(), states: new Set(), statuses: new Set(),
  });
  const count = activeFilterCount(value);

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="reports-filters"
        onClick={() => setOpen((v) => !v)}
        className="h-10 px-4 rounded-xl border hairline bg-white text-[12.5px] font-semibold text-neutral-900 hover:border-neutral-400 transition inline-flex items-center gap-2"
      >
        <svg className="h-3.5 w-3.5 text-neutral-500" fill="none" stroke="currentColor" strokeWidth="2.25" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 4h18M6 12h12M10 20h4" /></svg>
        Filters
        {count > 0 && (
          <span data-testid="reports-filter-count" className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-neutral-900 text-white text-[9.5px] font-bold">{count}</span>
        )}
        <ChevronDown className="h-3.5 w-3.5 text-neutral-400" />
      </button>
      {open && (
        <div
          data-testid="reports-filters-popover"
          className="absolute right-0 top-full mt-2 z-30 w-[480px] rounded-2xl border hairline bg-white shadow-[0_24px_60px_-24px_rgba(0,0,0,0.45)] overflow-hidden"
        >
          <div className="px-4 pt-4 pb-3 flex items-center justify-between border-b hairline-soft">
            <span className="display text-[14px] font-bold text-neutral-900">Filters</span>
            <div className="flex items-center gap-2">
              <button data-testid="reports-filters-clear" onClick={clear} className="text-[11.5px] font-bold text-neutral-500 hover:text-neutral-900 transition">Clear all</button>
              <button onClick={() => setOpen(false)} className="h-7 w-7 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-50 transition">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="p-4 space-y-4 max-h-[520px] overflow-y-auto">
            <FilterGroup label="Loan Officers">
              {team.length === 0 && <p className="text-[11.5px] text-neutral-400 italic">No team members loaded.</p>}
              {team.map((m) => {
                const on = value.loIds?.has(m.id);
                return (
                  <ChipToggle key={m.id} active={on} onClick={() => toggle('loIds', m.id)} testId={`filter-lo-${m.id}`}>
                    {m.name}
                  </ChipToggle>
                );
              })}
            </FilterGroup>
            <FilterGroup label="Loan Purpose">
              {PURPOSE_OPTIONS.map((p) => {
                const on = value.purposes?.has(p.key);
                return (
                  <ChipToggle key={p.key} active={on} onClick={() => toggle('purposes', p.key)} testId={`filter-purpose-${p.key}`}>
                    {p.label}
                  </ChipToggle>
                );
              })}
            </FilterGroup>
            {!hideChannel && (
              <FilterGroup label="Channel">
                {CHANNEL_OPTIONS.map((c) => {
                  const on = value.channels?.has(c.key);
                  return (
                    <ChipToggle key={c.key} active={on} onClick={() => toggle('channels', c.key)} testId={`filter-channel-${c.key}`}>
                      {c.label}
                    </ChipToggle>
                  );
                })}
              </FilterGroup>
            )}
            <FilterGroup label="Status">
              {STATUS_OPTIONS.map((s) => {
                const on = value.statuses?.has(s.key);
                return (
                  <ChipToggle key={s.key} active={on} onClick={() => toggle('statuses', s.key)} testId={`filter-status-${s.key}`}>
                    {s.label}
                  </ChipToggle>
                );
              })}
            </FilterGroup>
            {sourceOptions.length > 0 && (
              <FilterGroup label="Lead Source">
                {sourceOptions.map((s) => {
                  const on = value.sources?.has(s);
                  return (
                    <ChipToggle key={s} active={on} onClick={() => toggle('sources', s)} testId={`filter-source-${s}`}>
                      {s}
                    </ChipToggle>
                  );
                })}
              </FilterGroup>
            )}
            {!hideLender && lenderOptions.length > 0 && (
              <FilterGroup label="Lender">
                {lenderOptions.map((l) => {
                  const on = value.lenders?.has(l);
                  return (
                    <ChipToggle key={l} active={on} onClick={() => toggle('lenders', l)} testId={`filter-lender-${l}`}>
                      {l}
                    </ChipToggle>
                  );
                })}
              </FilterGroup>
            )}
            {stateOptions.length > 0 && (
              <FilterGroup label="State">
                {stateOptions.map((s) => {
                  const on = value.states?.has(s);
                  return (
                    <ChipToggle key={s} active={on} onClick={() => toggle('states', s)} testId={`filter-state-${s}`}>
                      {s}
                    </ChipToggle>
                  );
                })}
              </FilterGroup>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FilterGroup({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-2">{label}</div>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}
function ChipToggle({ active, onClick, testId, children }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border text-[11.5px] font-semibold transition',
        active
          ? 'bg-neutral-900 text-white border-neutral-900'
          : 'bg-white text-neutral-700 border-neutral-200 hover:border-neutral-400',
      )}
    >
      {active && <Check className="h-3 w-3" strokeWidth={3} />}
      {children}
    </button>
  );
}
